import React, { useState, useEffect } from 'react';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import SearchBar from './SearchBar';
import UserTable from './UserTable';
import UserModal from './UserModal';
import UserDetailModal from './UserDetailModal';
// IMPORTANTE: Usar los estilos estándar de Programas Académicos para mantener consistencia visual
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { getUsuariosPaged, getUsuario, createUsuario, updateUsuario, deleteUsuario } from '../../api/usuarios';
import Toast from '../DocentesScreen/Toast';
import { usePermissions } from '../../hooks/usePermissions';

function GestionUsuariosScreen() {
    // Handlers requeridos por el render (evitan errores no-undef)
    const handleNewUser = () => {
      setEditMode(false);
      setSelectedUser(null);
      setShowModal(true);
    };

    const handleSearch = (term) => {
      setSearchTerm(term);
    };

    const handleViewUser = async (userId) => {
      try {
        setLoading(true);
        const userData = await getUsuario(userId);
        setSelectedUser(userData);
        setShowDetailModal(true);
      } catch (error) {
        console.error('Error loading user:', error);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'Error al cargar el usuario' } }));
      } finally {
        setLoading(false);
      }
    };

    const handleEditUser = async (userId) => {
      try {
        setLoading(true);
        const userData = await getUsuario(userId);
        setSelectedUser(userData);
        setEditMode(true);
        setShowModal(true);
      } catch (error) {
        console.error('Error loading user:', error);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'Error al cargar el usuario' } }));
      } finally {
        setLoading(false);
      }
    };

    const handleDeleteUser = async (userId, userName) => {
      if (window.confirm(`¿Está seguro de eliminar el usuario "${userName || ''}"?`)) {
        try {
          setLoading(true);
          await deleteUsuario(userId);
          setRefreshTrigger(prev => prev + 1);
          window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Usuario eliminado correctamente' } }));
        } catch (error) {
          console.error('Error deleting user:', error);
          window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'Error al eliminar el usuario' } }));
        } finally {
          setLoading(false);
        }
      }
    };

    const handleCloseModal = () => {
      setShowModal(false);
      setEditMode(false);
      setSelectedUser(null);
    };

    const handleSaveUser = async (userData) => {
      try {
        setLoading(true);
        if (editMode && selectedUser) {
          await updateUsuario(selectedUser.id, userData);
          window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Usuario actualizado correctamente' } }));
        } else {
          await createUsuario(userData);
          window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Usuario creado correctamente' } }));
        }
        setRefreshTrigger(prev => prev + 1);
        handleCloseModal();
      } catch (error) {
        console.error('Error saving user:', error);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: `Error al guardar: ${error.message || 'Error desconocido'}` } }));
      } finally {
        setLoading(false);
      }
    };

    const handleCloseDetailModal = () => {
      setShowDetailModal(false);
      setSelectedUser(null);
    };
  // Hook para detectar si el sidebar está colapsado (patrón estándar del sistema)
  const { isCollapsed } = useSidebar();
  
  // Permisos del módulo Administración
  const { canEdit, canDelete } = usePermissions();
  const puedeEditar = canEdit('Administración');
  const puedeEliminar = canDelete('Administración');
  
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 10;

  const loadUsers = async (pageToLoad = 1, search = searchTerm, showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const data = await getUsuariosPaged({ page: pageToLoad, page_size: pageSize, search });
      setUsers(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || pageToLoad);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1, searchTerm, true);
  }, [refreshTrigger]);

  const handleSearchUsers = () => {
    loadUsers(1, searchTerm, true);
  };

  const handlePageChange = (nextPage) => {
    loadUsers(nextPage, searchTerm, true);
  };

  // ESTRUCTURA REDISEÑADA: Siguiendo exactamente el patrón de Programas Académicos
  // - mainContainer: contenedor principal
  // - layout: flex container con sidebar y content
  // - sidebarColumn: columna del sidebar (16% width)
  // - contentColumn: columna del contenido principal (84% width)
  // - contentArea: área con fondo gris claro
  // - contentWrapper: wrapper con padding para el contenido
  // - searchHeaderRow: fila con botón, buscador e institución
  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        {/* SIDEBAR: Columna izquierda fija con navegación */}
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>

        {/* CONTENT: Columna derecha con contenido principal */}
        <div className={styles.contentColumn}>
          {/* HEADER GLOBAL: mismo componente estándar usado en PantallaMaterias */}
          <HeaderGlobal title="Gestión de Usuarios" />
          <main className={styles.mainContent}>
            {/* CONTENT AREA: Fondo gris claro que envuelve todo el contenido */}
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Cargando usuarios..." />
                {/* SEARCH SECTION: Idéntico a PantallaMaterias */}
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Usuario..."
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          onKeyPress={e => {
                            if (e.key === 'Enter') handleSearchUsers();
                          }}
                        />
                      </div>
                      <button
                        className={styles.buscarButtonProgramas}
                        onClick={handleSearchUsers}
                      >
                        Buscar
                      </button>
                      {puedeEditar && (
                        <button
                          className={styles.newProgramButton}
                          onClick={handleNewUser}
                        >
                          + Nuevo Usuario
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* TABLA DE USUARIOS: Con estilo estándar del sistema */}
                <UserTable 
                  users={users}
                  onView={handleViewUser}
                  onEdit={handleEditUser}
                  onDelete={handleDeleteUser}
                  serverPaging={true}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalCount={totalCount}
                />
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* MODALES: Mantienen su estructura actual */}
      {showModal && (
        <UserModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSave={handleSaveUser}
          editMode={editMode}
          userData={selectedUser}
        />
      )}

      {showDetailModal && (
        <UserDetailModal
          isOpen={showDetailModal}
          onClose={handleCloseDetailModal}
          userData={selectedUser}
        />
      )}

      {/* TOAST: Sistema de notificaciones estándar */}
      <Toast />
    </div>
  );
}

export default GestionUsuariosScreen;
